import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isPrivatePath } from '@/shared/seo';

/**
 * Optional visitor analytics, off until an id is configured as an env var:
 * - VITE_GA_ID              Google Analytics 4 measurement id (G-XXXXXXXXXX)
 * - VITE_PLAUSIBLE_DOMAIN   Plausible site domain (e.g. ghostcable.tv)
 * Both are public ids, not secrets. Console, account, checkout and sign-in pages are never tracked.
 */

const GA_ID = String(import.meta.env.VITE_GA_ID ?? '').trim();
const PLAUSIBLE_DOMAIN = String(import.meta.env.VITE_PLAUSIBLE_DOMAIN ?? '').trim();

export const analyticsEnabled = () => /^G-[A-Z0-9]+$/i.test(GA_ID) || !!PLAUSIBLE_DOMAIN;

function addScript(src, attrs = {}) {
  if (document.head.querySelector(`script[src="${src}"]`)) return;
  const s = document.createElement('script');
  s.async = true;
  s.defer = true;
  s.src = src;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
}

function install() {
  if (window.__gcAnalyticsReady) return;
  window.__gcAnalyticsReady = true;

  if (/^G-[A-Z0-9]+$/i.test(GA_ID)) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      // gtag expects the arguments object itself
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', GA_ID, { send_page_view: false, anonymize_ip: true });
    addScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`);
  }

  if (PLAUSIBLE_DOMAIN) {
    window.plausible =
      window.plausible ||
      function plausible() {
        // eslint-disable-next-line prefer-rest-params
        (window.plausible.q = window.plausible.q || []).push(arguments);
      };
    addScript('https://plausible.io/js/script.manual.js', { 'data-domain': PLAUSIBLE_DOMAIN });
  }
}

function pageView(pathname) {
  const url = `${window.location.origin}${pathname}`;
  if (window.gtag && GA_ID) {
    window.gtag('event', 'page_view', { page_location: url, page_path: pathname, page_title: document.title });
  }
  if (window.plausible && PLAUSIBLE_DOMAIN) {
    window.plausible('pageview', { u: url });
  }
}

/** Custom event, e.g. track('Order placed', { plan: 'Demon' }). No-op when analytics is off. */
export function track(name, props = {}) {
  if (!analyticsEnabled() || typeof window === 'undefined') return;
  try {
    if (window.gtag && GA_ID) window.gtag('event', name.toLowerCase().replace(/\s+/g, '_'), props);
    if (window.plausible && PLAUSIBLE_DOMAIN) window.plausible(name, { props });
  } catch {
    // analytics never breaks the page
  }
}

/** Used once in RootLayout: loads the tracker on the first public page and counts each public page view. */
export function usePageAnalytics() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (!analyticsEnabled() || isPrivatePath(pathname)) return undefined;
    install();
    // Let the page set its title first.
    const t = setTimeout(() => pageView(pathname), 300);
    return () => clearTimeout(t);
  }, [pathname]);
}
